/**
 * When-to-use explainer
 *
 * Opened from the Treatment Support section to answer the one question the card
 * does not: whether this question belongs to the manufacturer or to a Primum
 * expert. Launch usability only, not medical content, so it does not go
 * through CAC.
 *
 * Two shapes, chosen by width rather than by platform. Wide, it is a dialog
 * holding two parallel columns, one per destination, so the column heading is
 * the answer and no per-question destination label is needed. Narrow, it is a
 * bottom sheet holding one block per destination. See `ExplainerSheet` for why
 * the sheet, and `DestinationBlock` for why the blocks.
 */

import { X } from 'lucide-react-native';
import { useEffect } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { MobileSheetHeader } from '@/components/MedicalOnDemand/mobile/MobileSheetHeader';
import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { WHEN_TO_USE_EXPLAINER, type PharmaProductSupportTeam } from '@/constants/medicalOnDemand';
import { useModTeamName } from '@/hooks/useModTeamName';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import {
  useWhenToUseExplainerCopy,
  type WhenToUseExplainerCopy,
} from '@/hooks/useWhenToUseExplainerCopy';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import { segmentClient } from '@/util/analytics';

type WhenToUseExplainerProps = {
  isOpen: boolean;
  /**
   * Where the link that opened this lives. The marker and link sit on the
   * Treatment Support section where one exists and on the card where it does
   * not, and this is how we learn which placement people actually use.
   */
  surface: 'section' | 'card';
  /**
   * Whether the new marker was showing when this was opened. The only way to
   * learn whether the marker drives opens, and unrecoverable once its window
   * closes.
   */
  isNewBadgeVisible?: boolean;
  onClose: () => void;
  /** Routes to the Treatment Support composer. Owned by the card, not here. */
  onAskTeam: () => void;
  /** Routes to the expert inquiry composer. Owned by the card, not here. */
  onAskExpert: () => void;
  team: PharmaProductSupportTeam | undefined;
};

type ExplainerBodyProps = {
  copy: WhenToUseExplainerCopy;
  intro: string;
};

type ExplainerActionsProps = {
  expertLabel: string;
  teamLabel: string;
  onAskExpert: () => void;
  onAskTeam: () => void;
};

/**
 * Everything about one destination in one block: its heading, what it is for,
 * then its own examples.
 *
 * This is the narrow-screen shape, and it exists because the comparison table
 * does not survive stacking. Collapsed to one column, each row becomes two
 * cells with nothing saying which side either belongs to, so a reader below the
 * breakpoint met "Patient-specific decisions" and "Dosing & step-up protocols"
 * as an unlabelled pair, and two "For example" headings with no destination
 * attached.
 *
 * Two layouts rather than one responsive layout, because the fix is structural
 * rather than cosmetic: side by side the destination is carried by the column,
 * stacked it has to be carried by the block.
 *
 * Tabs were the obvious alternative and are worse here. They hide one side, and
 * a comparison the reader has to hold in memory is not a comparison; defaulting
 * to the manufacturer would also leave most readers never seeing the expert
 * side at all.
 */
function DestinationBlock({
  heading,
  topics,
  examplesHeading,
  examples,
  action,
}: {
  heading: string;
  topics: string[];
  examplesHeading: string;
  examples: string[];
  action?: { label: string; variant: 'solid' | 'outline'; onPress: () => void };
}) {
  return (
    /*
     * One scale, strictly increasing, because every spacing bug in this modal
     * has been the same bug: a break between sections that measures smaller
     * than a break between items inside one, so the hierarchy inverts and a
     * reader groups the wrong things.
     *
     * From tightest to widest: 8px binds the examples label to its examples,
     * 12px separates single-line topics, 16px sits under the heading rule,
     * 24px separates examples that wrap to two and three lines and so has to
     * beat the ~20px of line spacing inside one, and 32px separates the tiers.
     * `StackedBody` then holds 40px between whole destinations, above all of
     * these.
     */
    <VStack className="gap-8">
      <VStack className="gap-4">
        <View className="border-b border-outline-300">
          <Text size="md" className="pb-2 font-semibold text-typography-900">
            {heading}
          </Text>
        </View>

        <VStack className="gap-3">
          {topics.map((topic) => (
            <Text key={topic} size="sm" className="text-typography-800">
              {topic}
            </Text>
          ))}
        </VStack>
      </VStack>

      <VStack className="gap-2">
        <Text size="xs" className="font-semibold uppercase tracking-wide text-typography-600">
          {examplesHeading}
        </Text>

        <VStack className="gap-6">
          {examples.map((question) => (
            <Text key={question} size="sm" className="text-typography-800">
              {`“${question}”`}
            </Text>
          ))}
        </VStack>
      </VStack>

      {action ? (
        <Button variant={action.variant} onPress={action.onPress} className="w-full">
          <ButtonText>{action.label}</ButtonText>
        </Button>
      ) : null}
    </VStack>
  );
}

/**
 * One row of the comparison, both sides at once.
 *
 * A row rather than two independent columns, because the rule under it has to
 * span the full width to read as a row separator. Building this per column
 * would put a rule under each cell and leave the two sides to line up only
 * while their text happened to wrap the same way.
 */
function CompareRow({ expert, team }: { expert: string; team: string }) {
  return (
    <VStack className="gap-1 md:flex-row md:gap-10">
      <Text size="sm" className="flex-1 text-typography-800">
        {expert}
      </Text>
      <Text size="sm" className="flex-1 text-typography-800">
        {team}
      </Text>
    </VStack>
  );
}

/**
 * One example: the question, and where it is best taken.
 *
 * Quoted, in roman, not italic.
 *
 * Quotation marks mark these as reported speech, which is what they are: things
 * a clinician would type. Italic claims emphasis instead, and it was the worst
 * of the three options tried here, because these run to three lines of clinical
 * shorthand, D-RVd in 1L, CAR-T in 2L, ECOG 2, dara-naive, and italic slants
 * exactly the hyphens, numerals and mixed-case abbreviations that are hardest
 * to read to begin with. Quotes carry the same meaning at no cost to legibility.
 *
 * The destination beside them is regular weight, not semibold. One rule across
 * both tables now that the examples have a real header row: headings are
 * semibold, cells are not. Semibold values under a semibold heading flatten
 * that distinction, and bold stops meaning anything when every cell in a column
 * carries it. What tracks a row here is the destination sharing a baseline with
 * the question's first line, which needs no weight to work.
 *
 * Same halves as the comparison row above, deliberately, so the two tables share
 * one grid. Sized to the content they would each be 60/40 and 50/50, and the
 * second column would land 69px apart between them. Two stacked grids that
 * nearly line up read as a mistake where two that clearly differ read as a
 * choice, and this modal shows both at once.
 *
 * The cost is a wider destination column than three words need. That is cheaper
 * than the stagger.
 */
function ExampleRow({ question, destination }: { question: string; destination: string }) {
  return (
    <VStack className="gap-0.5 md:flex-row md:gap-10">
      <Text size="sm" className="flex-1 text-typography-800">
        {`“${question}”`}
      </Text>
      <Text size="sm" className="flex-1 text-typography-900">
        {destination}
      </Text>
    </VStack>
  );
}

/** The wide shape: two columns, each one a destination. */
function ComparisonBody({ copy, intro }: ExplainerBodyProps) {
  return (
    <VStack space="md">
      <Text className="text-typography-700">{intro}</Text>

      {/*
        Rules run across the rows, not between the columns.

        A vertical rule would separate the two sides; these rules join them,
        which is what makes a row read as one comparison rather than as two
        lists that happen to sit alongside each other. The heading rule is
        heavier than the row rules for the same reason a table header is: it
        marks where the labels stop and the content starts.
      */}
      <VStack>
        <View className="border-b border-outline-300">
          <VStack className="gap-1 pb-2 md:flex-row md:gap-10">
            <Text size="md" className="flex-1 font-semibold text-typography-900">
              {copy.expertColumnHeading}
            </Text>
            <Text size="md" className="flex-1 font-semibold text-typography-900">
              {copy.teamColumnHeading}
            </Text>
          </VStack>
        </View>

        {/*
          Rows are single lines here, so 12px reads as a clear break without
          needing a rule under each one.
        */}
        <VStack className="gap-3 pt-3">
          {copy.bulletRows.map((row) => (
            <CompareRow key={row.expert} expert={row.expert} team={row.team} />
          ))}
        </VStack>
      </VStack>

      {/*
        The section break has to beat the row gap inside a section, or the
        hierarchy inverts and "For example" reads as another row of the
        comparison. Measured: rows sit 20px apart, so this clears it.
      */}
      <VStack className="gap-1 pt-4">
        {/*
          A header row in the same treatment as the comparison above, so the two
          tables read as two of the same thing rather than as a table and a
          loose list.

          "For example" sits in the question column rather than floating above
          the row, which is where it was: a section label on one baseline and a
          column label on another, in the same band of the modal, which looked
          like a mistake because it was one.
        */}
        <View className="border-b border-outline-300">
          <VStack className="gap-1 pb-2 md:flex-row md:gap-10">
            <Text size="md" className="flex-1 font-semibold text-typography-900">
              {copy.examplesHeading}
            </Text>
            <Text size="md" className="flex-1 font-semibold text-typography-900">
              {copy.exampleVerdictLabel}
            </Text>
          </VStack>
        </View>

        {/*
          Wider than the comparison rows above, and for a reason rather than for
          rhythm: these questions wrap to two or three lines, so the gap between
          rows has to be unmistakably larger than the line spacing inside one.
          At a tighter value a wrapped question reads as two rows.

          Widened alongside the column gutter. An earlier pass held the columns
          tighter than the rows on the rule that a table read across should bind
          horizontally first. That rule is real and it was overweighted here:
          the destination is short and top-aligned to the question's first line,
          which binds each row on its own without help from the gutter. What the
          narrow gutter actually did was crowd the longest questions against the
          destination beside them.
        */}
        <VStack className="gap-6 pt-3">
          {copy.examples.map((example) => (
            <ExampleRow
              key={example.question}
              question={example.question}
              destination={WHEN_TO_USE_EXPLAINER.exampleDestinationNames[example.destination]}
            />
          ))}
        </VStack>
      </VStack>
    </VStack>
  );
}

/**
 * The narrow shape: one block per destination. See `DestinationBlock`.
 *
 * `actions` is optional because this body serves two containers. In the sheet
 * each block ends with its own button, which is the point of the shape: the
 * door sits where the argument for it finishes, so a reader who recognizes
 * their question in a block acts from there rather than scrolling on to a
 * footer. In the dialog's narrow fallback the footer already holds both
 * buttons, and adding them here would show each one twice.
 */
function StackedBody({
  copy,
  intro,
  actions,
}: ExplainerBodyProps & {
  actions?: { expert: () => void; team: () => void; expertLabel: string; teamLabel: string };
}) {
  /**
   * Every approved example, the same set the wide layout shows.
   *
   * An earlier pass showed one per destination to keep both blocks inside one
   * screen. It bought about 70px on the smallest phone we support and cost two
   * of the four questions Vicki wrote, which made us the ones deciding which
   * example a clinician sees. The examples are what a reader recognizes their
   * own question in, so that was the wrong thing to spend. The sheet scrolls;
   * that is what sheets do.
   */
  const questionsFor = (destination: 'expert' | 'team') =>
    copy.examples
      .filter((example) => example.destination === destination)
      .map((example) => example.question);

  return (
    <VStack space="md">
      <Text className="text-typography-700">{intro}</Text>

      {/*
        The manufacturer goes first here, and only here.
        
        Wide, the expert column leads and the order carries an argument: the
        two destinations are equals and the reader meets both before choosing.
        Stacked, there is no "both at once" to preserve. Whatever is first is
        what a reader sees before scrolling, and the reader arrived from a J&J
        card by tapping a link about asking J&J, so leading with the expert
        answers a question they did not ask.
        
        The expert block sits directly beneath rather than behind a tab, which
        is what keeps this an ordering choice rather than a hiding one.
      */}
      <VStack className="gap-10">
        <DestinationBlock
          heading={copy.teamColumnHeading}
          topics={copy.bulletRows.map((row) => row.team)}
          examplesHeading={copy.examplesHeading}
          examples={questionsFor('team')}
          action={
            actions
              ? { label: actions.teamLabel, variant: 'solid', onPress: actions.team }
              : undefined
          }
        />
        <DestinationBlock
          heading={copy.expertColumnHeading}
          topics={copy.bulletRows.map((row) => row.expert)}
          examplesHeading={copy.examplesHeading}
          examples={questionsFor('expert')}
          action={
            actions
              ? { label: actions.expertLabel, variant: 'outline', onPress: actions.expert }
              : undefined
          }
        />
      </VStack>
    </VStack>
  );
}

/**
 * Two actions, because the modal asks the reader to reach one of two
 * conclusions and previously only one of them had a door. Someone who decided
 * "Primum Expert" had a manufacturer button and a close icon.
 *
 * Both actions now go somewhere. The expert one used to close the modal on the
 * reasoning that the directory was already behind it, which put the reader back
 * where they started still holding the question. It routes to the expert
 * composer instead, so each conclusion leads to the place it can be acted on.
 *
 * J&J stays primary, since the reader arrived from the J&J card and that is the
 * path they were already on.
 *
 * Order is expert first, J&J second, and it holds on both axes for two separate
 * reasons. Side by side it puts the primary rightmost, which is the convention
 * Apple and Material both use for the confirming action in a dialog. And it
 * maps to the comparison above, where expert is the left column and J&J the
 * right, so each action sits under the column that argued for it.
 *
 * Stacked on a narrow screen the mapping survives, because the destinations
 * stack in the same order, and it also leaves the primary lowest and so nearest
 * the thumb.
 */
function ExplainerActions({
  expertLabel,
  teamLabel,
  onAskExpert,
  onAskTeam,
}: ExplainerActionsProps) {
  return (
    <>
      <Button variant="outline" onPress={onAskExpert} className="w-full md:w-auto">
        <ButtonText>{expertLabel}</ButtonText>
      </Button>
      <Button onPress={onAskTeam} className="w-full md:w-auto">
        <ButtonText>{teamLabel}</ButtonText>
      </Button>
    </>
  );
}

export function WhenToUseExplainer({
  isOpen,
  onClose,
  onAskTeam,
  onAskExpert,
  surface,
  isNewBadgeVisible,
  team,
}: WhenToUseExplainerProps) {
  const teamName = useModTeamName(team?.name);

  const insets = useSafeAreaInsets();

  /**
   * Width, not platform, picks the shape.
   *
   * The sibling MOD sheets branch on `!isWeb`, and that is the wrong test for
   * this one. What breaks the two-column comparison is a narrow viewport, and
   * the narrow viewport we actually watched it break on was a phone browser at
   * 375px, which `isWeb` calls web and would have left on the dialog. Branching
   * on width covers the phone browser and the native app together, and it
   * matches the `md` breakpoint the columns themselves use.
   */
  const isCompact = useIsCompactLayout();

  /**
   * Flag-authored copy layered over the approved in-repo set, field by field.
   * Nothing set means nothing changes.
   */
  const copy = useWhenToUseExplainerCopy();

  /**
   * Fires on open rather than on render, since the component is mounted the
   * whole time and only `isOpen` distinguishes a view from an existence.
   */
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    segmentClient
      .track(ANALYTICS_EVENTS.MOD_WHEN_TO_ASK_OPENED, {
        surface,
        new_badge_visible: Boolean(isNewBadgeVisible),
      })
      ?.catch(() => {});
  }, [isOpen, surface, isNewBadgeVisible]);

  /**
   * Which door a reader takes after reading the comparison, which is the
   * measure this modal exists to move. A split that lands entirely on the
   * manufacturer means either the copy is one-sided or the expert door is not
   * being seen, and neither is visible without this.
   */
  const trackCta = (destination: 'expert' | 'team') => {
    segmentClient
      .track(ANALYTICS_EVENTS.MOD_WHEN_TO_ASK_CTA_CLICKED, { surface, destination })
      ?.catch(() => {});
  };

  const handleClose = () => {
    segmentClient.track(ANALYTICS_EVENTS.MOD_WHEN_TO_ASK_DISMISSED, { surface })?.catch(() => {});

    onClose();
  };

  if (!teamName) {
    return null;
  }

  const intro = copy.intro ?? WHEN_TO_USE_EXPLAINER.getIntro(teamName);

  const handleAskExpert = () => {
    trackCta('expert');

    onAskExpert();
  };

  const handleAskTeam = () => {
    trackCta('team');

    onAskTeam();
  };

  /**
   * A sheet on narrow screens, not a centered dialog.
   *
   * It is the house pattern for MOD on a phone, `ProductDetailsSheet` and
   * `ProductTeamDetailsSheet` both, so a reader who has opened one of those
   * already knows this one swipes down. It also puts the two actions at the
   * bottom of the screen rather than at the bottom of a floating box, which on
   * a phone is the difference between reachable and not, and this modal exists
   * to get someone through one of those two doors.
   *
   * The actions sit outside the scroll view. The stacked comparison is long
   * enough to scroll on a phone, and a reader who has decided partway down
   * should not have to scroll to the end to act on it.
   */
  if (isCompact) {
    return (
      <Actionsheet isOpen={isOpen} onClose={handleClose}>
        <ActionsheetBackdrop />
        {/*
          Both bounds, not just the upper one. The sheet's children are
          `flex-1`, so without a `minHeight` there is no height for them to
          fill and the whole thing collapses to its drag handle at the bottom
          of the screen. Same pair as `ProductTeamDetailsSheet`.
        */}
        <ActionsheetContent
          className="w-full rounded-t-2xl p-0"
          style={{ maxHeight: '95%', minHeight: '85%', paddingBottom: insets.bottom }}>
          <ActionsheetDragIndicatorWrapper>
            <ActionsheetDragIndicator />
          </ActionsheetDragIndicatorWrapper>

          <VStack className="w-full flex-1">
            <VStack className="px-6 pb-4">
              {/*
                Same `h2-xl` as the dialog. `h2-md` resolves to `text-base`,
                which is the body size, so the title read as a line of prose
                with a close button beside it rather than as the heading of the
                thing that just opened.
              */}
              <MobileSheetHeader title={copy.title} titleSize="h2-xl" onClose={handleClose} />
            </VStack>

            <Divider className="flex-shrink-0" />

            <ActionsheetScrollView className="min-h-0 flex-1" contentContainerClassName="px-6 py-6">
              <StackedBody
                copy={copy}
                intro={intro}
                actions={{
                  expertLabel: copy.expertCtaLabel,
                  teamLabel: WHEN_TO_USE_EXPLAINER.getCtaLabel(teamName),
                  expert: handleAskExpert,
                  team: handleAskTeam,
                }}
              />
            </ActionsheetScrollView>
          </VStack>
        </ActionsheetContent>
      </Actionsheet>
    );
  }

  return (
    <Modal isOpen={isOpen} size="lg" onClose={handleClose}>
      <ModalBackdrop />

      {/* `lg` caps at 640px, which is tight for two columns of clinical questions. */}
      <ModalContent className="max-h-[85vh] md:max-w-[760px]">
        <ModalHeader>
          {/*
            `h2-xl` because that is what this app's modals use, Delete draft,
            Contact Support and Edit Disclosures among them. This one sat at
            `h2-md` and read smaller than the house norm.

            It no longer echoes the link that opens it. The link says "When to
            ask J&J Medical", which is what the reader wanted to know; the
            title asks who to ask, which is what the modal actually answers,
            and naming only J&J here would frame a two-destination comparison
            as a one-destination explainer. The intro sentence and the second
            block carry the match instead.
          */}
          <Heading size="h2-xl">{copy.title}</Heading>
          <ModalCloseButton hitSlop={ICON_BUTTON_HIT_SLOP} onPress={handleClose}>
            <Icon as={X} size="md" className="text-typography-500" />
          </ModalCloseButton>
        </ModalHeader>

        {/*
          The dialog picks its own body by CSS rather than inheriting the
          container's choice, because the two resolve at different times.
          `useIsCompactLayout` reads at mount and does not flip on a live
          resize, which is how every consumer of it in this app behaves;
          dragging a desktop window from 1280 to 400 keeps the dialog. That is
          a fine thing to degrade to, but only if the body inside it narrows
          too. Left on the columns it would collapse to the orphaned pairs
          `DestinationBlock` exists to prevent.
        */}
        <ModalBody className="py-3">
          <View className="hidden md:flex">
            <ComparisonBody copy={copy} intro={intro} />
          </View>

          <View className="md:hidden">
            <StackedBody copy={copy} intro={intro} />
          </View>
        </ModalBody>

        <ModalFooter className="my-3 flex-col gap-2 md:flex-row md:justify-end">
          <ExplainerActions
            expertLabel={copy.expertCtaLabel}
            teamLabel={WHEN_TO_USE_EXPLAINER.getCtaLabel(teamName)}
            onAskExpert={handleAskExpert}
            onAskTeam={handleAskTeam}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
